import { getApiBaseUrl } from '../config/api';

// FEAT-039 — Admin Caregiver Verification client.
// Backs the CaregiverVerificationQueuePage, the VerificationReviewSidebar,
// and the Verify / Reject (with reason) controls.

export type CredentialDocStatus = 'PENDING_REVIEW' | 'ACCEPTED' | 'REJECTED';
export type CredentialDocType =
  | 'STATE_CERT'
  | 'TRAINING_CERT'
  | 'TRANSCRIPT'
  | 'OTHER';

export interface QueueCredentialDocument {
  id: string;
  docType: CredentialDocType;
  title: string;
  status: CredentialDocStatus;
  uploadedAt: string;
  mimeType: string;
  expiresAt: string | null;
}

export interface QueueCaregiver {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  stateCode: string | null;
  stateLicenseNumber: string | null;
  credentialDocuments: QueueCredentialDocument[];
  latestDecision: {
    id: string;
    decision: 'VERIFIED' | 'REJECTED';
    decidedAt: string;
    reason: string | null;
  } | null;
}

export interface VerificationQueueResponse {
  caregivers: QueueCaregiver[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface VerificationDecisionRow {
  id: string;
  caregiverId: string;
  adminId: string;
  decision: 'VERIFIED' | 'REJECTED';
  reason: string | null;
  decidedAt: string;
}

class CaregiverVerificationService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async listQueue(params: { page?: number; limit?: number } = {}): Promise<VerificationQueueResponse> {
    const baseUrl = await getApiBaseUrl();
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    const url = `${baseUrl}/admin/caregivers/verification-queue${
      qs.toString() ? `?${qs.toString()}` : ''
    }`;
    const response = await fetch(url, { headers: this.getAuthHeaders() });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error?.message || 'Failed to load verification queue');
    }
    return response.json();
  }

  async verify(caregiverId: string, reason?: string): Promise<{ decision: VerificationDecisionRow }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/admin/caregivers/${caregiverId}/verify`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(reason ? { reason } : {}),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error?.message || 'Failed to verify caregiver');
    }
    return response.json();
  }

  async reject(caregiverId: string, reason: string): Promise<{ decision: VerificationDecisionRow }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/admin/caregivers/${caregiverId}/reject-verification`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ reason }),
      },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error?.message || 'Failed to reject verification');
    }
    return response.json();
  }
}

export const caregiverVerificationService = new CaregiverVerificationService();
